const OPTIMAL_SETUP_STORAGE_PREFIX = 'scarlet-optimal-setup-v1';
const OPTIMAL_SETUP_HANDOFF_PREFIX = 'scarlet-optimal-setup-handoff-v1';

export const OPTIMAL_SETUP_ITEM_IDS = [
  'primary_schedule',
  'active_term',
  'commuter_preference',
  'push_alerts',
  'sms_alerts',
  'location',
  'calendar',
  'widgets',
] as const;

export type OptimalSetupItemId = (typeof OPTIMAL_SETUP_ITEM_IDS)[number];
export type CommuterPreference = 'bus' | 'drive' | 'walk' | 'unsure';
export type OptimalSetupEntrypoint = 'onboarding' | 'settings';
export type SystemPromptStage = 'about_to_display' | 'displayed' | 'unavailable';
export type PermissionResult = 'granted' | 'denied' | 'unavailable';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type SearchParamsLike = Pick<URLSearchParams, 'get'>;

type NotificationPermissionApi = {
  permission: NotificationPermission;
  requestPermission: () => Promise<NotificationPermission>;
};

type GeolocationApi = {
  getCurrentPosition: (
    success: () => void,
    error: () => void,
    options?: PositionOptions,
  ) => void;
};

export type OptimalSetupProgress = {
  version: 1;
  completed: OptimalSetupItemId[];
  dismissed: OptimalSetupItemId[];
  commuterPreference: CommuterPreference | null;
};

export const EMPTY_OPTIMAL_SETUP_PROGRESS: OptimalSetupProgress = {
  version: 1,
  completed: [],
  dismissed: [],
  commuterPreference: null,
};

function isItemId(value: unknown): value is OptimalSetupItemId {
  return typeof value === 'string' && OPTIMAL_SETUP_ITEM_IDS.includes(value as OptimalSetupItemId);
}

function isCommuterPreference(value: unknown): value is CommuterPreference {
  return value === 'bus' || value === 'drive' || value === 'walk' || value === 'unsure';
}

function normalizeOwnerUserId(ownerUserId: string | null | undefined): string | null {
  if (typeof ownerUserId !== 'string') return null;
  const normalized = ownerUserId.trim();
  if (!normalized || normalized.length > 128 || !/^[A-Za-z0-9._:-]+$/.test(normalized)) return null;
  return normalized;
}

export function optimalSetupProgressStorageKey(ownerUserId: string | null | undefined): string | null {
  const owner = normalizeOwnerUserId(ownerUserId);
  return owner ? `${OPTIMAL_SETUP_STORAGE_PREFIX}:${owner}` : null;
}

export function optimalSetupCalendarHandoffStorageKey(ownerUserId: string | null | undefined): string | null {
  const owner = normalizeOwnerUserId(ownerUserId);
  return owner ? `${OPTIMAL_SETUP_HANDOFF_PREFIX}:${owner}` : null;
}

export function canStartOptimalSetupCalendarHandoff(
  entrypoint: OptimalSetupEntrypoint,
  ownerUserId: string | null | undefined,
): boolean {
  return entrypoint === 'settings' && optimalSetupCalendarHandoffStorageKey(ownerUserId) !== null;
}

export function parseOptimalSetupProgress(value: unknown): OptimalSetupProgress {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return EMPTY_OPTIMAL_SETUP_PROGRESS;
  const candidate = value as Partial<OptimalSetupProgress>;
  return {
    version: 1,
    completed: Array.from(new Set(Array.isArray(candidate.completed) ? candidate.completed.filter(isItemId) : [])),
    dismissed: Array.from(new Set(Array.isArray(candidate.dismissed) ? candidate.dismissed.filter(isItemId) : [])),
    commuterPreference: isCommuterPreference(candidate.commuterPreference) ? candidate.commuterPreference : null,
  };
}

export function updateOptimalSetupProgress(
  progress: OptimalSetupProgress,
  item: OptimalSetupItemId,
  state: 'completed' | 'dismissed',
): OptimalSetupProgress {
  const completed = progress.completed.filter((entry) => entry !== item);
  const dismissed = progress.dismissed.filter((entry) => entry !== item);
  if (state === 'completed') completed.push(item);
  else dismissed.push(item);
  return { ...progress, completed, dismissed };
}

export function completedSetupCount(progress: OptimalSetupProgress): number {
  return progress.completed.length;
}

export function readOptimalSetupProgress(
  storage: StorageLike,
  ownerUserId: string | null | undefined,
): OptimalSetupProgress {
  const key = optimalSetupProgressStorageKey(ownerUserId);
  if (!key) return EMPTY_OPTIMAL_SETUP_PROGRESS;
  try {
    return parseOptimalSetupProgress(JSON.parse(storage.getItem(key) || 'null'));
  } catch {
    return EMPTY_OPTIMAL_SETUP_PROGRESS;
  }
}

export function writeOptimalSetupProgress(
  storage: StorageLike,
  ownerUserId: string | null | undefined,
  progress: OptimalSetupProgress,
): boolean {
  const key = optimalSetupProgressStorageKey(ownerUserId);
  if (!key) return false;
  storage.setItem(key, JSON.stringify(parseOptimalSetupProgress(progress)));
  return true;
}

export function startOptimalSetupCalendarHandoff(
  storage: StorageLike,
  ownerUserId: string | null | undefined,
  entrypoint: OptimalSetupEntrypoint,
): boolean {
  const key = optimalSetupCalendarHandoffStorageKey(ownerUserId);
  if (!key) return false;
  storage.setItem(key, JSON.stringify({
    version: 1,
    item: 'calendar',
    entrypoint,
  }));
  return true;
}

export function clearOptimalSetupCalendarHandoff(
  storage: StorageLike,
  ownerUserId: string | null | undefined,
): boolean {
  const key = optimalSetupCalendarHandoffStorageKey(ownerUserId);
  if (!key) return false;
  storage.removeItem(key);
  return true;
}

export function isOptimalSetupCalendarTarget(searchParams: SearchParamsLike): boolean {
  return searchParams.get('export') === 'true';
}

/**
 * The Scheduling destination consumes an exact, device-local handoff marker.
 * A query parameter alone is not evidence that the setup flow initiated it.
 */
export function consumeOptimalSetupCalendarHandoff(
  storage: StorageLike,
  ownerUserId: string | null | undefined,
  searchParams: SearchParamsLike,
): OptimalSetupEntrypoint | null {
  if (!isOptimalSetupCalendarTarget(searchParams)) return null;
  const key = optimalSetupCalendarHandoffStorageKey(ownerUserId);
  if (!key) return null;
  const raw = storage.getItem(key);
  if (!raw) return null;
  try {
    const candidate = JSON.parse(raw) as Record<string, unknown>;
    if (
      candidate.version !== 1
      || candidate.item !== 'calendar'
      || (candidate.entrypoint !== 'onboarding' && candidate.entrypoint !== 'settings')
    ) return null;
    storage.removeItem(key);
    return candidate.entrypoint;
  } catch {
    return null;
  }
}

export function recordVerifiedOptimalSetupUse(
  storage: StorageLike,
  ownerUserId: string | null | undefined,
  item: 'calendar',
): OptimalSetupProgress | null {
  const key = optimalSetupProgressStorageKey(ownerUserId);
  if (!key) return null;
  const current = readOptimalSetupProgress(storage, ownerUserId);
  const next = updateOptimalSetupProgress(current, item, 'completed');
  storage.setItem(key, JSON.stringify(next));
  return next;
}

export async function requestNotificationPermission(
  api: NotificationPermissionApi | null,
  onPromptStage: (stage: SystemPromptStage) => void,
): Promise<PermissionResult> {
  if (!api) {
    onPromptStage('unavailable');
    return 'unavailable';
  }
  if (api.permission === 'granted' || api.permission === 'denied') return api.permission;
  onPromptStage('about_to_display');
  let pendingChoice: Promise<NotificationPermission>;
  try {
    pendingChoice = api.requestPermission();
  } catch {
    onPromptStage('unavailable');
    return 'unavailable';
  }
  onPromptStage('displayed');
  try {
    const choice = await pendingChoice;
    return choice === 'granted' ? 'granted' : 'denied';
  } catch {
    onPromptStage('unavailable');
    return 'unavailable';
  }
}

export async function requestGeolocationPermission(
  api: GeolocationApi | null,
  currentPermission: PermissionState | 'unknown',
  onPromptStage: (stage: SystemPromptStage) => void,
): Promise<PermissionResult> {
  if (!api) {
    onPromptStage('unavailable');
    return 'unavailable';
  }
  if (currentPermission === 'denied') return 'denied';

  return new Promise((resolve) => {
    if (currentPermission === 'prompt' || currentPermission === 'unknown') {
      onPromptStage('about_to_display');
    }
    try {
      api.getCurrentPosition(
        () => resolve('granted'),
        () => resolve('denied'),
        { enableHighAccuracy: false, maximumAge: 10 * 60_000, timeout: 8_000 },
      );
      if (currentPermission === 'prompt' || currentPermission === 'unknown') {
        onPromptStage('displayed');
      }
    } catch {
      onPromptStage('unavailable');
      resolve('unavailable');
    }
  });
}
